import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { http } from "../api/http";
import type { User } from "../types";

interface PermissionSnapshot {
    isSuperuser: boolean;
    roleKeys: string[];
    permissions: string[];
}

interface AuthContextValue {
    user: User | null;
    isAuthenticated: boolean;
    isSuperuser: boolean;
    roleKeys: string[];
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    /// Auto-serviço: o próprio usuário altera nome e/ou senha.
    updateProfile: (input: { name?: string; password?: string }) => Promise<void>;
    hasPermission: (permission: string) => boolean;
    /// Recarrega o snapshot de permissões (opcionalmente no escopo de um armazém).
    refreshPermissions: (warehouseId?: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const emptySnapshot: PermissionSnapshot = { isSuperuser: false, roleKeys: [], permissions: [] };
const storedUser = () => {
    try {
        const value = localStorage.getItem("wms.user");
        return value ? (JSON.parse(value) as User) : null;
    } catch {
        return null;
    }
};

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(storedUser);
    const [token, setToken] = useState(() => localStorage.getItem("wms.token"));
    const [snapshot, setSnapshot] = useState<PermissionSnapshot>(emptySnapshot);

    // Snapshot resolvido pelo back-end (RBAC). É a fonte da verdade da UI.
    const refreshPermissions = useCallback(async (warehouseId?: number) => {
        if (!localStorage.getItem("wms.token")) return;
        try {
            const { data } = await http.get<PermissionSnapshot>("/auth/permissions", {
                params: warehouseId ? { warehouseId } : {},
            });
            setSnapshot({
                isSuperuser: Boolean(data.isSuperuser),
                roleKeys: data.roleKeys ?? [],
                permissions: data.permissions ?? [],
            });
        } catch {
            /* mantém o snapshot atual em caso de falha */
        }
    }, []);

    const login = useCallback(
        async (username: string, password: string) => {
            const { data } = await http.post<{ token: string; user: User }>("/auth/login", { username, password });
            localStorage.setItem("wms.token", data.token);
            localStorage.setItem("wms.user", JSON.stringify(data.user));
            setToken(data.token);
            setUser(data.user);
            await refreshPermissions();
        },
        [refreshPermissions],
    );

    const logout = () => {
        localStorage.removeItem("wms.token");
        localStorage.removeItem("wms.user");
        setToken(null);
        setUser(null);
        setSnapshot(emptySnapshot);
    };

    const updateProfile = useCallback(async (input: { name?: string; password?: string }) => {
        const { data } = await http.patch<User>("/auth/me", input);
        setUser((prev) => {
            const next = { ...(prev as User), ...data };
            localStorage.setItem("wms.user", JSON.stringify(next));
            return next;
        });
    }, []);

    // Ao recarregar a página com token válido, resolve o snapshot novamente.
    useEffect(() => {
        if (token) void refreshPermissions();
    }, [token, refreshPermissions]);

    const value = useMemo(
        () => ({
            user,
            isAuthenticated: Boolean(token),
            isSuperuser: snapshot.isSuperuser,
            roleKeys: snapshot.roleKeys,
            login,
            logout,
            updateProfile,
            refreshPermissions,
            hasPermission: (permission: string) => snapshot.isSuperuser || snapshot.permissions.includes(permission),
        }),
        [token, user, snapshot, login, updateProfile, refreshPermissions],
    );
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const value = useContext(AuthContext);
    if (!value) throw new Error("useAuth deve ser usado dentro de AuthProvider");
    return value;
}
